if (!customElements.get('shoppable-images-carousel')) {
  const DESKTOP_MQ = '(min-width: 769px)';
  const MANUAL_SPEED = 400;
  const MOBILE_THUMBS_PER_VIEW = 1.15;

  class ShoppableImagesCarousel extends HTMLElement {
    constructor() {
      super();
      this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
      this.handleThumbClick = this.handleThumbClick.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.followThumbs = this.followThumbs.bind(this);
      this.releaseThumbs = this.releaseThumbs.bind(this);
      this.dragSource = null;
      this.ignoreThumbClick = false;
      this.syncingThumbs = false;
    }

    connectedCallback() {
      this.layout = this.querySelector('[data-shoppable-layout]');
      this.mainEl = this.querySelector('[data-shoppable-main]');
      this.mainWrapper = this.querySelector('[data-shoppable-main-wrapper]');
      this.thumbsEl = this.querySelector('[data-shoppable-thumbs]');
      this.thumbsWrapper = this.querySelector('[data-shoppable-thumbs-wrapper]');
      this.scrollbarEl = this.querySelector('[data-shoppable-scrollbar]');
      this.statusEl = this.querySelector('[data-shoppable-status]');
      this.desktopMq = window.matchMedia(DESKTOP_MQ);
      this.autoplaySpeed = Math.max(1, Number(this.dataset.autoplaySpeed) || 8) * 1000;
      this.thumbsPerView = Math.max(1, Number(this.dataset.thumbsPerView) || 2);

      this.mainSlides = this.mainWrapper
        ? [...this.mainWrapper.querySelectorAll('[data-shoppable-slide]')]
        : [];
      this.thumbSlides = [];

      if (this.mainSlides.length < 2) {
        this.classList.add('is-ready');
        return;
      }

      this.desktopMq.addEventListener('change', this.handleBreakpointChange);
      this.thumbsEl?.addEventListener('click', this.handleThumbClick);
      this.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);

      this.waitForSwiper();
    }

    disconnectedCallback() {
      this.cancelSwiperLoad?.();
      clearTimeout(this.resizeTimer);
      this.desktopMq?.removeEventListener('change', this.handleBreakpointChange);
      this.thumbsEl?.removeEventListener('click', this.handleThumbClick);
      this.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      this.destroySwipers();
      this.initialized = false;
    }

    waitForSwiper() {
      this.cancelSwiperLoad?.();
      this.cancelSwiperLoad = theme.prepareSwiper(this, () => this.setup());
    }

    setup() {
      if (this.initialized) return;
      this.initialized = true;
      this.classList.add('is-ready');
      this.update();
    }

    isDesktop() {
      return this.desktopMq.matches;
    }

    getGap() {
      if (!this.layout) return 8;
      const fromVar = parseFloat(getComputedStyle(this.layout).getPropertyValue('--shoppable-carousel-gap'));
      if (fromVar > 0) return fromVar;
      return parseFloat(getComputedStyle(this.layout).columnGap || getComputedStyle(this.layout).gap) || 8;
    }

    update() {
      this.destroySwipers();
      this.buildThumbs();
      this.initThumbsSwiper();
      if (this.isDesktop()) this.initMainSwiper();
      this.syncUi(this.thumbsSwiper?.realIndex || 0);
    }

    buildThumbs() {
      if (!this.thumbsWrapper) return;

      this.thumbsWrapper.replaceChildren();

      this.mainSlides.forEach((slide, index) => {
        const source = slide.querySelector('hotspots-image');
        const thumb = document.createElement('div');
        thumb.className = `shoppable-carousel__thumb${index === 0 ? ' is-active' : ''}`;
        thumb.setAttribute('data-shoppable-thumb', '');
        thumb.setAttribute('role', 'button');
        thumb.setAttribute('tabindex', '0');
        thumb.setAttribute('aria-label', slide.dataset.title || `Image ${index + 1}`);
        thumb.setAttribute('aria-current', index === 0 ? 'true' : 'false');

        if (source) {
          const clone = source.cloneNode(true);
          clone.querySelectorAll('.shoppable-carousel__media img').forEach((image) => {
            const auto = image.loading === 'lazy' ? 'auto, ' : '';
            image.sizes = `${auto}(min-width: 769px) ${50 / this.thumbsPerView}vw, 87vw`;
          });
          clone.querySelectorAll('.hotspot.is-active').forEach((hotspot) => {
            hotspot.classList.remove('is-active');
            hotspot.querySelector('.hotspot__btn')?.setAttribute('aria-expanded', 'false');
            hotspot.querySelector('.hotspot__card')?.setAttribute('aria-hidden', 'true');
          });
          thumb.appendChild(clone);
        }

        this.thumbsWrapper.appendChild(thumb);
      });

      this.thumbSlides = [...this.thumbsWrapper.children];
    }

    initMainSwiper() {
      if (!this.mainEl || !this.mainWrapper) return;

      this.mainEl.classList.add('swiper');
      this.mainWrapper.classList.add('swiper-wrapper');
      [...this.mainWrapper.children].forEach((child) => child.classList.add('swiper-slide'));

      const autoplay = this.dataset.autoplay === 'true' && !this.prefersReducedMotion();

      this.mainSwiper = new window.Swiper(this.mainEl, {
        autoplay: autoplay ? {
          delay: this.autoplaySpeed,
          disableOnInteraction: true,
          pauseOnMouseEnter: true
        } : false,
        grabCursor: true,
        keyboard: { enabled: true, onlyInViewport: true },
        longSwipesRatio: 0.3,
        noSwipingClass: 'hotspot',
        slidesPerView: 1,
        spaceBetween: 0,
        speed: MANUAL_SPEED,
        threshold: 5,
        watchOverflow: true,
        on: {
          touchStart: () => {
            this.dragSource = 'main';
          },
          slideChange: () => {
            this.closeHotspots();
            const index = this.mainSwiper.realIndex;
            if (this.dragSource !== 'thumbs') this.scrollThumbsTo(index);
            this.syncUi(index);
          },
          touchEnd: () => {
            if (this.dragSource === 'main') this.dragSource = null;
          },
          transitionEnd: () => {
            if (this.dragSource === 'main') this.dragSource = null;
          }
        }
      });
    }

    initThumbsSwiper() {
      if (!this.thumbsEl || !this.thumbsWrapper || this.thumbSlides.length < 2) return;

      this.thumbsEl.classList.add('swiper');
      this.thumbsWrapper.classList.add('swiper-wrapper');
      [...this.thumbsWrapper.children].forEach((child) => child.classList.add('swiper-slide'));

      const autoplay = this.dataset.autoplay === 'true' && !this.prefersReducedMotion() && !this.isDesktop();

      this.thumbsSwiper = new window.Swiper(this.thumbsEl, {
        a11y: { enabled: false },
        autoplay: autoplay ? {
          delay: this.autoplaySpeed,
          disableOnInteraction: true,
          pauseOnMouseEnter: true
        } : false,
        breakpoints: {
          769: { slidesPerView: this.thumbsPerView }
        },
        freeMode: {
          enabled: true,
          momentum: true,
          momentumBounce: false,
          sticky: false
        },
        grabCursor: true,
        noSwipingClass: 'hotspot',
        resistanceRatio: 0.85,
        scrollbar: this.scrollbarEl ? {
          dragSize: 'auto',
          draggable: true,
          el: this.scrollbarEl,
          hide: false,
          snapOnRelease: false
        } : false,
        slideToClickedSlide: false,
        slidesPerView: MOBILE_THUMBS_PER_VIEW,
        spaceBetween: this.getGap(),
        speed: MANUAL_SPEED,
        threshold: 5,
        watchOverflow: true,
        watchSlidesProgress: true,
        on: {
          touchStart: () => {
            this.dragSource = 'thumbs';
            this.ignoreThumbClick = false;
          },
          sliderMove: () => {
            this.ignoreThumbClick = true;
            this.followThumbs();
          },
          slideChange: this.followThumbs,
          scrollbarDragStart: () => {
            this.dragSource = 'thumbs';
            this.ignoreThumbClick = true;
          },
          scrollbarDragMove: this.followThumbs,
          scrollbarDragEnd: this.releaseThumbs,
          touchEnd: this.releaseThumbs,
          transitionEnd: this.followThumbs
        }
      });

      if (this.scrollbarEl && this.scrollbarEl.previousElementSibling !== this.thumbsEl) {
        this.thumbsEl.after(this.scrollbarEl);
      }

      if (this.isDesktop()) this.thumbsEl.setAttribute('aria-hidden', 'true');
      else this.thumbsEl.removeAttribute('aria-hidden');
    }

    indexFromThumbs() {
      const swiper = this.thumbsSwiper;
      if (!swiper) return 0;

      const grid = swiper.slidesGrid;
      if (!grid?.length) return swiper.realIndex || 0;

      const translate = -swiper.getTranslate();
      let index = 0;

      for (let i = 1; i < grid.length; i += 1) {
        const midpoint = (grid[i - 1] + grid[i]) / 2;
        if (translate >= midpoint) index = i;
      }

      return Math.min(index, this.mainSlides.length - 1);
    }

    followThumbs() {
      if (!this.thumbsSwiper || this.dragSource === 'main' || this.syncingThumbs) return;

      const index = this.indexFromThumbs();
      this.closeHotspots();

      if (this.mainSwiper && this.mainSwiper.activeIndex !== index) {
        this.dragSource = this.dragSource || 'thumbs';
        this.mainSwiper.slideTo(index, 0);
      }

      this.syncUi(index);
    }

    releaseThumbs() {
      this.followThumbs();

      const dragged = this.ignoreThumbClick;
      window.setTimeout(() => {
        this.ignoreThumbClick = false;
        if (this.dragSource === 'thumbs') this.dragSource = null;
      }, dragged ? 50 : 0);
    }

    scrollThumbsTo(index) {
      if (!this.thumbsSwiper || this.dragSource === 'thumbs') return;

      const pos = this.thumbsSwiper.slidesGrid?.[index];
      this.syncingThumbs = true;

      if (typeof pos === 'number') {
        this.thumbsSwiper.setTransition(MANUAL_SPEED);
        this.thumbsSwiper.setTranslate(-pos);
        this.thumbsSwiper.updateActiveIndex();
        this.thumbsSwiper.updateSlidesClasses();
      } else {
        this.thumbsSwiper.slideTo(index);
      }

      this.syncingThumbs = false;
    }

    destroySwipers() {
      this.dragSource = null;
      this.ignoreThumbClick = false;
      this.syncingThumbs = false;

      if (this.thumbsSwiper) {
        this.thumbsSwiper.destroy(true, true);
        this.thumbsSwiper = null;
      }
      if (this.mainSwiper) {
        this.mainSwiper.destroy(true, true);
        this.mainSwiper = null;
      }

      this.mainEl?.classList.remove('swiper');
      this.mainWrapper?.classList.remove('swiper-wrapper');
      this.thumbsEl?.classList.remove('swiper');
      this.thumbsWrapper?.classList.remove('swiper-wrapper');
      this.thumbsEl?.style.removeProperty('height');

      this.mainWrapper?.querySelectorAll('.swiper-slide').forEach((el) => el.classList.remove('swiper-slide'));
      this.thumbsWrapper?.querySelectorAll('.swiper-slide').forEach((el) => el.classList.remove('swiper-slide'));
    }

    syncUi(index) {
      const total = this.mainSlides.length;
      const safeIndex = Math.max(0, Math.min(index, total - 1));

      this.thumbSlides.forEach((thumb, thumbIndex) => {
        const isActive = thumbIndex === safeIndex;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      if (this.statusEl) {
        this.statusEl.textContent = `${safeIndex + 1} ${this.statusEl.dataset.of || 'of'} ${total}`;
      }
    }

    handleThumbClick(event) {
      if (event.target.closest('.hotspot')) return;
      if (this.ignoreThumbClick) return;
      if (this.thumbsSwiper && this.thumbsSwiper.allowClick === false) return;

      const thumb = event.target.closest('[data-shoppable-thumb]');
      if (!thumb) return;

      const index = this.thumbSlides.indexOf(thumb);
      if (index < 0) return;

      this.slideTo(index);
    }

    handleKeydown(event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.slideBy(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.slideBy(-1);
      }
    }

    slideTo(index) {
      this.dragSource = 'main';
      if (this.mainSwiper) this.mainSwiper.slideTo(index);
      this.scrollThumbsTo(index);
      this.syncUi(index);
    }

    slideBy(delta) {
      const current = this.thumbsSwiper?.realIndex ?? this.mainSwiper?.realIndex ?? 0;
      const next = Math.max(0, Math.min(this.mainSlides.length - 1, current + delta));
      this.slideTo(next);
    }

    handleBlockSelect(event) {
      if (!this.contains(event.target)) return;

      const slide = event.target.closest('[data-shoppable-slide]');
      if (!slide) return;

      const index = this.mainSlides.indexOf(slide);
      if (index < 0) return;

      this.slideTo(index);
    }

    handleBreakpointChange() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.destroySwipers();
        this.initialized = false;
        this.waitForSwiper();
      }, 100);
    }

    closeHotspots() {
      this.querySelectorAll('hotspots-image').forEach((el) => {
        if (el.activeHotspot && typeof el.closeActiveHotspot === 'function') {
          el.closeActiveHotspot({ type: 'slidechange' });
        }
      });
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  customElements.define('shoppable-images-carousel', ShoppableImagesCarousel);
}
